import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Tags,
  Boxes,
  LogOut,
  Store,
  Menu,
  X,
  ImagePlus,
  ClipboardList,
  Megaphone,
  MessageCircle,
  Share2,
  Ticket,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/bulk-images", label: "Bulk Photos", icon: ImagePlus },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/types", label: "Types", icon: Tags },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/offer", label: "Offer Popup", icon: Megaphone },
  { to: "/admin/social", label: "Social Links", icon: Share2 },
  { to: "/admin/whatsapp-leads", label: "WhatsApp Leads", icon: MessageCircle },
];

const Denied = ({ expired }) => (
  <div className="theme-admin flex min-h-screen items-center justify-center bg-brand-cream px-6 text-center">
    <div data-testid="admin-denied">
      <p className="font-serif text-3xl text-brand-charcoal">{expired ? "Your session has expired" : "Admin access required"}</p>
      <p className="mt-3 text-sm text-brand-stone">
        {expired ? "Please sign in again to continue to the Admin Studio via the " : "Sign in with the owner account via the "}
        <Link to="/login?next=/admin" data-testid="admin-denied-login-link" className="text-brand-orange underline-offset-4 hover:underline">login page</Link>.
      </p>
    </div>
  </div>
);

const AuthFailed = ({ message, onRetry }) => (
  <div className="theme-admin flex min-h-screen items-center justify-center bg-brand-cream px-6 text-center">
    <div data-testid="admin-auth-error">
      <p className="font-serif text-3xl text-brand-charcoal">We couldn't verify your session</p>
      <p className="mt-3 text-sm text-brand-stone">{message}</p>
      <div className="mt-6 flex justify-center gap-3">
        <button type="button" onClick={onRetry} data-testid="admin-auth-retry" className="bg-brand-charcoal px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cream hover:bg-brand-orange">Retry</button>
        <Link to="/login?next=/admin" data-testid="admin-auth-login" className="border border-brand-sand px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-charcoal hover:border-brand-orange">Sign in again</Link>
      </div>
    </div>
  </div>
);

export const AdminLayout = ({ title, subtitle, actions, children }) => {
  const { user, ready, authError, sessionExpired, retryAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!ready)
    return (
      <div className="theme-admin flex min-h-screen items-center justify-center bg-brand-cream" data-testid="admin-loading">
        <p className="font-serif text-2xl text-brand-charcoal">Loading…</p>
      </div>
    );
  if (!user && authError) return <AuthFailed message={authError} onRetry={retryAuth} />;
  if (!user || user.role !== "admin") return <Denied expired={sessionExpired && !user} />;

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1" data-testid="admin-nav">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={`nav-${label.toLowerCase()}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wide transition-colors ${
              isActive
                ? "bg-brand-orange text-brand-cream"
                : "text-brand-cream/70 hover:bg-brand-cream/10 hover:text-brand-cream"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );

  const SidebarInner = ({ onNav }) => (
    <div className="flex h-full flex-col bg-brand-charcoal">
      <div className="px-6 py-7">
        <p className="font-serif text-xl tracking-[0.35em] text-brand-cream">HESHAURA</p>
        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-brand-gold">Admin Studio</p>
      </div>
      <div className="mt-2 flex-1 px-2">
        <NavItems onClick={onNav} />
      </div>
      <div className="border-t border-brand-cream/10 px-2 py-4">
        <a
          href="/"
          data-testid="admin-view-store"
          className="flex items-center gap-3 px-4 py-3 text-sm text-brand-cream/70 transition-colors hover:text-brand-cream"
        >
          <Store className="h-4 w-4" /> View store
        </a>
        <button
          type="button"
          data-testid="admin-logout"
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="flex w-full items-center gap-3 px-4 py-3 text-sm text-brand-cream/70 transition-colors hover:text-brand-orange"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
        <p className="mt-3 truncate px-4 text-[11px] text-brand-cream/40" data-testid="admin-user-email">{user.email}</p>
      </div>
    </div>
  );

  return (
    <div className="theme-admin min-h-screen bg-brand-cream font-sans text-brand-charcoal">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 md:block">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-brand-charcoal/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">
            <SidebarInner onNav={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="md:pl-64">
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-brand-sand bg-brand-surface/90 px-5 py-4 backdrop-blur sm:px-8">
          <button
            type="button"
            className="text-brand-charcoal md:hidden"
            data-testid="admin-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-serif text-2xl tracking-tight text-brand-charcoal sm:text-3xl" data-testid="admin-page-title">
              {title}
            </h1>
            {subtitle && <p className="mt-0.5 truncate text-xs text-brand-stone">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
        <main className="px-5 py-8 sm:px-8">{children}</main>
      </div>
    </div>
  );
};
